/**
 * Sezione Notifiche - Preferenze di notifica per Seller
 * Auto-save abilitato per UX migliorata
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getErrorMessage } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { 
  Bell, 
  Mail,
  Smartphone,
  Loader2,
  Cloud
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationsSection = ({ user, t }) => {
  const [notificationPrefs, setNotificationPrefs] = useState({
    notifications_enabled: true,
    email_enabled: true,
    push_enabled: true,
    new_order: { in_app: true, email: true, push: true },
    order_confirmed: { in_app: true, email: false, push: false },
    new_message: { in_app: true, email: false, push: true },
    low_stock: { in_app: true, email: true, push: false }
  });
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (user?.role === 'seller') {
      fetchNotificationPrefs();
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [user]);

  const fetchNotificationPrefs = async () => {
    try {
      const res = await axios.get(`${API}/notifications/preferences`);
      if (res.data) {
        setNotificationPrefs(prev => ({ ...prev, ...res.data }));
      }
      setTimeout(() => {
        isFirstLoad.current = false;
      }, 300);
    } catch (error) {
      console.error('Error fetching notification prefs:', error);
    }
  };

  // Auto-save silenzioso
  const performAutoSave = useCallback(async (prefs: typeof notificationPrefs) => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/preferences`, prefs);
      setLastSaved(new Date());
    } catch (error: any) {
      console.error('Auto-save error:', error);
    } finally {
      setSaving(false);
    }
  }, []);

  // Aggiorna preferenze con auto-save
  const updatePrefs = useCallback((newPrefs: typeof notificationPrefs) => {
    setNotificationPrefs(newPrefs);
    
    if (isFirstLoad.current) return;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Auto-save immediato per switch (sono azioni intenzionali)
    saveTimeoutRef.current = setTimeout(() => {
      performAutoSave(newPrefs);
    }, 500);
  }, [performAutoSave]);

  if (user?.role !== 'seller') return null;

  const notificationEvents = [
    { key: 'new_order', label: 'Nuovo Ordine', description: 'Quando ricevi un nuovo ordine' },
    { key: 'order_confirmed', label: 'Ordine Confermato', description: 'Quando confermi un ordine' },
    { key: 'new_message', label: 'Nuovo Messaggio', description: 'Quando ricevi un messaggio in chat' },
    { key: 'low_stock', label: 'Stock Basso', description: 'Quando un prodotto scende sotto la soglia' }
  ];

  return (
    <Card data-testid="notifications-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {t('settings.notifications')}
            </CardTitle>
            <CardDescription>
              {t('settings.notificationsDescription')}
            </CardDescription>
          </div>
          {/* Auto-save indicator */}
          <div className="text-sm">
            {saving ? (
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Salvataggio...
              </span>
            ) : lastSaved ? (
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <Cloud className="h-3.5 w-3.5 text-green-500" />
                Salvato
              </span>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Toggle Globali */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              <Label>In-App</Label>
            </div>
            <Switch
              checked={notificationPrefs.notifications_enabled}
              onCheckedChange={(checked) => updatePrefs({ ...notificationPrefs, notifications_enabled: checked })}
              data-testid="notifications-enabled-switch"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <Label>Email</Label>
            </div>
            <Switch
              checked={notificationPrefs.email_enabled}
              onCheckedChange={(checked) => updatePrefs({ ...notificationPrefs, email_enabled: checked })}
              data-testid="email-enabled-switch"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              <Label>Push</Label>
            </div>
            <Switch
              checked={notificationPrefs.push_enabled}
              onCheckedChange={(checked) => updatePrefs({ ...notificationPrefs, push_enabled: checked })}
              data-testid="push-enabled-switch"
            />
          </div>
        </div>

        {/* Eventi */}
        <div className="space-y-3">
          <Label className="text-sm font-medium text-muted-foreground">Preferenze per evento</Label>
          {notificationEvents.map(event => (
            <div key={event.key} className="flex items-center justify-between py-2 border-b last:border-0">
              <div>
                <div className="font-medium text-sm">{event.label}</div>
                <div className="text-xs text-muted-foreground">{event.description}</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <Bell className="h-3 w-3 text-muted-foreground" />
                  <Switch
                    checked={notificationPrefs[event.key]?.in_app ?? true}
                    onCheckedChange={(checked) => updatePrefs({ 
                      ...notificationPrefs, 
                      [event.key]: { ...notificationPrefs[event.key], in_app: checked }
                    })}
                    disabled={!notificationPrefs.notifications_enabled}
                    data-testid={`${event.key}-in-app-switch`}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Mail className="h-3 w-3 text-muted-foreground" />
                  <Switch
                    checked={notificationPrefs[event.key]?.email ?? false}
                    onCheckedChange={(checked) => updatePrefs({ 
                      ...notificationPrefs, 
                      [event.key]: { ...notificationPrefs[event.key], email: checked }
                    })}
                    disabled={!notificationPrefs.email_enabled}
                    data-testid={`${event.key}-email-switch`}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Smartphone className="h-3 w-3 text-muted-foreground" />
                  <Switch
                    checked={notificationPrefs[event.key]?.push ?? false}
                    onCheckedChange={(checked) => updatePrefs({ 
                      ...notificationPrefs, 
                      [event.key]: { ...notificationPrefs[event.key], push: checked }
                    })}
                    disabled={!notificationPrefs.push_enabled}
                    data-testid={`${event.key}-push-switch`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationsSection;
